import { useEffect, useRef } from 'react'
import { IconButton } from '@/shared/ui/IconButton'
import { CloseIcon } from '@/shared/ui/icons'
import { useMessages } from './use-language'
import { LANGUAGE_IDS, LANGUAGE_NAMES, type LanguageId } from './types'

interface LanguagePickerProps {
  /** 지금 화면이 서 있는 언어. 고른 값이 없으면 협상 결과가 여기 든다. */
  effective: LanguageId
  /** 사용자가 고른 값. 고른 적이 없으면 없다. */
  chosen: LanguageId | null
  /** 시스템 설정을 고르면 값을 비운다는 뜻으로 null이 온다. */
  onChoose: (next: LanguageId | null) => void
  onClose: () => void
}

/**
 * 언어를 고르는 자리.
 *
 * 좁은 폭에서는 아래에서 올라오는 시트로, 넓은 폭에서는 화면 가운데 대화로 선다. 별도 화면으로
 * 밀지 않는다. 밀면 되돌아오는 길이 하나 생기고 설정 안에 층이 한 겹 든다.
 *
 * 목록의 값은 언제나 그 언어의 자기 이름이다. 고르는 사람은 지금 화면을 읽지 못해서 고르므로
 * 읽지 못하는 언어로 적은 이름은 아무것도 알려 주지 않는다. 순서도 고정한다. 화면 언어에 따라
 * 자기 언어를 맨 위로 올리면 되돌아온 사람이 아까 본 자리를 잃는다.
 *
 * 항목마다 그 항목의 언어를 속성으로 단다. 한국어 화면 낭독기가 English를 한국어 발음으로 읽지
 * 않게 한다. 시스템 설정 항목만 화면 언어를 따라 갈리므로 그 속성을 달지 않는다.
 *
 * 선택 표시는 고른 값을 가리킨다. 고른 적이 없으면 시스템 설정에 서고 지금 무슨 언어인지는
 * 그 항목의 둘째 줄이 말한다.
 */
export function LanguagePicker({ effective, chosen, onChoose, onClose }: LanguagePickerProps) {
  const m = useMessages()
  const panel = useRef<HTMLDivElement>(null)

  // 열리면 지금 값에 포커스를 둔다. 목록을 소리로 듣는 사람이 어디에 서 있는지부터 안다.
  useEffect(() => {
    panel.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center sm:items-center">
      {/* 뒤를 눌러도 닫힌다. 이 자리는 기호가 아니라 면이라 이름을 갖지 않는다. */}
      <div onClick={onClose} aria-hidden="true" className="absolute inset-0 bg-black/30" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-picker-title"
        className="bg-surface rounded-card relative w-full max-w-md px-5 pt-5 pb-7 shadow-lg sm:pb-5"
      >
        <div className="-mr-2.5 flex items-start justify-between gap-4">
          <h2 id="language-picker-title" className="text-lg font-medium">
            {m.settings.language.sheetTitle}
          </h2>
          {/* 덮은 층을 걷는 자리라 가위표다. 화면을 옮기는 왼쪽 화살표와 뜻이 갈린다. */}
          <IconButton label={m.settings.language.close} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        {/* 되묻는 대신 잃을 것이 없다는 사실을 미리 알린다. 남긴 글이 번역될까 봐 언어를 못
            바꾸는 일을 이 한 줄이 막는다. */}
        <p className="text-ink-muted mt-1.5 text-sm leading-[1.6]">
          {m.settings.language.sheetHint}
        </p>

        <ul
          role="radiogroup"
          aria-labelledby="language-picker-title"
          className="mt-4 flex flex-col"
        >
          {/*
            맨 위에 서고 이 항목만 두 줄이다. 둘째 줄에 지금 풀리는 언어의 자기 이름을 세운다.
            항목 하나가 지금 무슨 언어로 보이는지를 가리는 것이 이 자리를 두지 않기로 했던
            까닭이었고 값을 함께 세워 그것을 막는다.

            고르는 일은 언어를 고르는 것이 아니라 고른 값을 비우는 일이다. 화면에 지운다는
            말을 쓰지 않는다.
          */}
          <li>
            <button
              type="button"
              role="radio"
              aria-checked={chosen === null}
              onClick={() => onChoose(null)}
              className="border-border/60 pressable flex w-full items-center justify-between gap-4 border-b px-1 py-3 text-left"
            >
              <span className="flex flex-col">
                <span>{m.settings.language.system}</span>
                <span className="text-ink-muted text-sm" lang={effective}>
                  {LANGUAGE_NAMES[effective]}
                </span>
              </span>
              {chosen === null && <span aria-hidden="true">✓</span>}
            </button>
          </li>

          {LANGUAGE_IDS.map((id) => (
            <li key={id}>
              <button
                type="button"
                role="radio"
                aria-checked={chosen === id}
                lang={id}
                onClick={() => onChoose(id)}
                className="border-border/60 pressable flex w-full items-center justify-between border-b px-1 py-3.5 text-left last:border-b-0"
              >
                <span>{LANGUAGE_NAMES[id]}</span>
                {chosen === id && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
